import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import Slider from 'react-slick';
import { Instagram } from 'react-content-loader';
import IconPrevArrow from '../IconPrevArrow/IconPrevArrow';
import IconNextArrow from '../IconNextArrow/IconNextArrow';
import '../../styles/slick/slick.css';
import '../../styles/slick/slick-theme.css';
import css from './ImageSlider.module.css';
import { isArrayLength } from '../../util/genericHelpers';
import imagePlaceholder from '../../assets/imagePlaceholder.png';
import useNearViewport from '../../hooks/useNearViewport';

function ImageSlider(props) {
  const [viewportRef, nearViewport] = useNearViewport();
  const {
    images,
    isMobile,
    pushToListingPage = () => {},
    isSectionHero = false,
    imgAlt = '',
    pathParams = {},
  } = props;

  const handleDragStart = e => e.preventDefault();
  const stopPropagation = e => e.stopPropagation();
  const stopNavigation = e => {
    e.preventDefault();
    e.stopPropagation();
  };
  const handleImageError = e => {
    if (e?.currentTarget?.src !== imagePlaceholder) {
      e.currentTarget.src = imagePlaceholder;
    }
  };

  let items =
    images &&
    images.map((item, index) => (
      <img
        key={index}
        src={item || imagePlaceholder}
        loading={isSectionHero ? 'eager' : 'lazy'}
        decoding="async"
        alt={imgAlt}
        onDragStart={handleDragStart}
        onError={handleImageError}
        role="presentation"
        className={classNames({
          [css.images]: !isSectionHero,
          [css.heroImages]: isSectionHero,
        })}
        onClick={() => pushToListingPage(pathParams?.id)}
      />
    ));

  if (pathParams?.id) {
    items =
      images &&
      images.map((item, index) => (
        <div onClick={() => pushToListingPage(pathParams?.id)} key={index}>
          {items[index]}
        </div>
      ));
  }

  // State to keep track of whether component is mounted
  const [isMounted, setIsMounted] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [swipeInProgress, setSwipeInProgress] = useState(false);
  const swipeTimeoutRef = useRef(null);

  useEffect(() => {
    setIsMounted(true);
    return () => {
      setIsMounted(false);
    };
  }, []);

  // Cancel subscriptions and asynchronous tasks when component is unmounted
  useEffect(() => {
    return () => {
      setIsMounted(false);
    };
  }, []);

  useEffect(() => {
    return () => {
      if (swipeTimeoutRef.current) {
        clearTimeout(swipeTimeoutRef.current);
      }
    };
  }, []);

  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  const handleSwipe = () => {
    setSwipeInProgress(true);
    if (swipeTimeoutRef.current) {
      clearTimeout(swipeTimeoutRef.current);
    }
    swipeTimeoutRef.current = setTimeout(() => {
      setSwipeInProgress(false);
      swipeTimeoutRef.current = null;
    }, 300);
  };

  const handleClickCapture = event => {
    if (swipeInProgress) {
      stopNavigation(event);
    }
  };

  function RenderNextArrow(props) {
    const { className, style, onClick } = props;
    const handleClick = event => {
      event.preventDefault();
      event.stopPropagation();
      if (onClick) {
        onClick(event);
      }
    };
    return (
      <div className={className} onClick={handleClick}>
        <IconNextArrow />
      </div>
    );
  }

  function RenderPrevArrow(props) {
    const { className, style, onClick } = props;
    const handleClick = event => {
      event.preventDefault();
      event.stopPropagation();
      if (onClick) {
        onClick(event);
      }
    };
    return (
      <div className={className} onClick={handleClick}>
        <IconPrevArrow />
      </div>
    );
  }

  const settings = {
    dots: isSectionHero ? false : true,
    fade: true,
    arrows: isHovered,
    infinite: true,
    slidesToShow: 1,
    slidesToScroll: 1,
    adaptiveHeight: isSectionHero,
    autoplay: false,
    lazyLoad: 'ondemand',
    speed: 800,
    pauseOnHover: true,
    onSwipe: handleSwipe,
    appendDots: dots => <div onClick={stopPropagation}>{dots}</div>,
    nextArrow: <RenderNextArrow />,
    prevArrow: <RenderPrevArrow />,
  };

  if (isMobile) {
    settings.arrows = false;
    settings.dots = true;
  }

  return (
    <div
      className={css.carouselContainer}
      ref={viewportRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClickCapture={handleClickCapture}
    >
      {isMounted && (isSectionHero || nearViewport) ? (
        <Slider className={css.cardSlider} {...settings}>
          {isArrayLength(items) ? items : <Instagram />}
        </Slider>
      ) : (
        <div className={isSectionHero ? css.heroImages : css.images} aria-hidden="true" />
      )}
    </div>
  );
}

export default ImageSlider;
